import { Box, Button, Collapse, Dialog, Stack } from "@mui/material";
import { Ticket, TicketPrintTemplate } from "../../../services/swagger";
import useConfirm from "../../../hooks/useConfirm";
import FormTemplate from "../../templates/FormTemplate";
import { useTranslation } from "react-i18next";
import FormInput from "../../molecules/FormInput";
import { useEffect, useState } from "react";
import { PrintService } from "../../../services/api";
import SignatureInput from "../../molecules/SignatureInput";
import { getBlobFromBase64 } from "../../../helpers/print";
import useRefresh from "../../../hooks/useRefresh";
import SectionTitle from "../../atoms/SectionTitle";
import { useMutation } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";

interface TicketPrintDialogProps {
  open: boolean;
  ticket: Ticket;
  onClose?: () => any;
}

export default function TicketPrintDialog(props: TicketPrintDialogProps) {
  const { t } = useTranslation("TicketPrintDialog");
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en");
  const [sendCopy, setSendCopy] = useState(false);
  const [sendForward, setSendForward] = useState(false);
  const [template, setTemplate] = useState("");
  const [copyEmail, setCopyEmail] = useState("");
  const [selectedTemplate, setSelectedTemplate] =
    useState<TicketPrintTemplate>();
  const [giverSignature, setGiverSignature] = useState("");
  const [receiverSignature, setReceiverSignature] = useState("");
  const [templates, setTemplates] = useState<TicketPrintTemplate[]>([]);

  const refresh = useRefresh();

  const { mutate: loadTicketPrintTemplateList, isPending } = useMutation({
    mutationFn: async () => {
      const res = await PrintService.getTicketPrintTemplateList();
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) {
        setTemplates(data);
        if (data.length > 0) {
          setTemplate(data[0]._id || "");
        }
      }
    },
  });

  const { mutate: previewOriginal, isPending: isPreviewingOriginal } =
    useMutation({
      mutationFn: async () => {
        if (!template || !props.ticket._id) return null;
        const res = await PrintService.createTicketPrintPreview({
          templateId: template,
          ticketId: props.ticket._id,
          createTicketPrintPreviewBody: {
            language,
            signatures: {
              giver: giverSignature,
              receiver: receiverSignature,
            },
          },
        });
        return res?.data?.data;
      },
      onSuccess: (data) => {
        if (data?.original) {
          window.open(getBlobFromBase64(data.original), "_blank");
        }
      },
    });

  const { mutate: previewCopy, isPending: isPreviewingCopy } = useMutation({
    mutationFn: async () => {
      if (!template) return null;
      if (!selectedTemplate?.options.copyTemplate) return null;
      if (!props.ticket._id) return null;

      const res = await PrintService.createTicketPrintPreview({
        templateId: template,
        ticketId: props.ticket._id,
        createTicketPrintPreviewBody: {
          language,
          signatures: {
            giver: giverSignature,
            receiver: receiverSignature,
          },
        },
      });

      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data?.copy) {
        window.open(getBlobFromBase64(data.copy), "_blank");
      }
    },
  });
  const handleSubmit = () => {
    confirm(async () => {
      if (!template) return;
      if (!props.ticket._id) return;
      const res = await PrintService.createTicketPrint({
        templateId: template,
        ticketId: props.ticket._id,
        createTicketPrintBody: {
          name,
          language,
          copyEmail,
          sendForward,
          signatures: {
            giver: giverSignature,
            receiver: receiverSignature,
          },
        },
      });
      if (res?.data?.success) {
        refresh();
        props.onClose?.();
      }
    });
  };

  useEffect(() => {
    if (template) {
      setSelectedTemplate(templates.find((t) => t._id === template));
    } else setSelectedTemplate(undefined);
  }, [template]);

  useEffect(() => {
    if (props.open) loadTicketPrintTemplateList();
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      PaperProps={{
        sx: {
          minWidth: { xs: 0.9, sm: 500, md: 600 },
          height: { xs: 500, md: "fit-content" },
          maxHeight: 1,
        },
      }}
    >
      <FormTemplate
        title={t("PrintTicket")}
        submitButtonText={
          (copyEmail && sendCopy) || sendForward ? t("SaveAndSend") : t("Save")
        }
        onCancel={props.onClose}
        onSubmit={handleSubmit}
        confirm
      >
        <Stack spacing={2}>
          <FormInput
            loading={isPending}
            label={t("Name")}
            field="name"
            type="string"
            value={name}
            onChange={(v) => setName(v as string)}
            required
          />
          <FormInput
            loading={isPending}
            label={t("Language")}
            field="language"
            type="list"
            list={[
              { name: "En", value: "en" },
              { name: "Tr", value: "tr" },
            ]}
            value={language}
            onChange={(v) => setLanguage(v as string)}
            required
          />
          <FormInput
            loading={isPending}
            label={t("Template")}
            field="template"
            type="list"
            list={templates.map((e) => ({
              name: `${e.name}${
                e?.options?.copyTemplate
                  ? ` | ${t("Copy")}: ${
                      templates.find((t) => t._id === e.options.copyTemplate)
                        ?.name
                    }`
                  : ""
              }`,
              value: e._id || "",
            }))}
            value={template}
            onChange={(v) => setTemplate(v as string)}
            required
          />

          {(selectedTemplate?.options?.forwardEmails?.length || 0) > 0 && (
            <FormInput
              loading={isPending}
              label={t("SendToForwardedEmails")}
              field="sendForward"
              type="switch"
              value={sendForward}
              onChange={(v) => setSendForward(v as boolean)}
            />
          )}
          {selectedTemplate?.options.copyTemplate && (
            <>
              <FormInput
                loading={isPending}
                label={t("SendCopy")}
                field="sendCopy"
                type="switch"
                value={sendCopy}
                onChange={(v) => setSendCopy(v as boolean)}
              />
              <Collapse in={sendCopy}>
                <FormInput
                  loading={isPending}
                  label={t("CopyEmail")}
                  field="copyEmail"
                  type="string"
                  required={sendCopy}
                  value={copyEmail}
                  onChange={(v) => setCopyEmail(v as string)}
                />
              </Collapse>
            </>
          )}
          {selectedTemplate?.options.signatureRequired && (
            <Stack spacing={2}>
              <SectionTitle>{t("Signatures")}</SectionTitle>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems="stretch"
              >
                <Box
                  flexGrow={1}
                  border={1}
                  borderColor="divider"
                  borderRadius={2}
                  p={2}
                  minWidth={240}
                >
                  <SignatureInput
                    label={t("GiverSignature")}
                    field="giverSignature"
                    value={giverSignature}
                    onChange={(v) => setGiverSignature(v)}
                  />
                </Box>
                <Box
                  flexGrow={1}
                  border={1}
                  borderColor="divider"
                  borderRadius={2}
                  p={2}
                  minWidth={240}
                >
                  <SignatureInput
                    label={t("ReceiverSignature")}
                    field="receiverSignature"
                    value={receiverSignature}
                    onChange={(v) => setReceiverSignature(v)}
                  />
                </Box>
              </Stack>
            </Stack>
          )}
          <SectionTitle>{t("Preview")}</SectionTitle>
          <Stack direction={"row"} spacing={2}>
            <Button
              variant="outlined"
              loading={isPreviewingOriginal}
              onClick={() => previewOriginal()}
            >
              {t("OriginalPreview")}
            </Button>
            {selectedTemplate?.options.copyTemplate && (
              <Button
                variant="outlined"
                loading={isPreviewingCopy}
                onClick={() => previewCopy()}
              >
                {t("CopyPreview")}
              </Button>
            )}
          </Stack>
        </Stack>
      </FormTemplate>
    </Dialog>
  );
}
